import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Warning } from "@phosphor-icons/react";
import type { DuplicateUnit } from "../hooks/useIndexationData";

interface DuplicateUnitsPopoverProps {
  count: number;
  duplicateUnits: DuplicateUnit[];
  /** Formatted display value for the tile (may be "—" during loading) */
  displayValue: string;
  isLoading: boolean;
}

/**
 * KPI tile for "Units met meerdere contracten".
 * When count > 0 the tile is clickable and opens a Popover listing each unit.
 * - Unit rows with a buildingId navigate to /building/{buildingId}/verhuur on click.
 * - Unit rows without a buildingId show a tooltip "Geen gebouw bekend".
 * - Clicking a building row closes the popover before navigating.
 */
export const DuplicateUnitsPopover: React.FC<DuplicateUnitsPopoverProps> = ({
  count,
  duplicateUnits,
  displayValue,
  isLoading,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isClickable = count > 0;

  const handleBuildingNav = (buildingId: string) => {
    setOpen(false);
    navigate(`/building/${buildingId}/verhuur`);
  };

  const tile = (
    <div
      className={`rounded-lg border bg-card px-4 py-3 flex flex-col gap-1 transition-colors ${
        isClickable
          ? "cursor-pointer hover:bg-muted/50 border-brick-secondary-fine"
          : "cursor-default"
      }`}
    >
      {/* Big number */}
      <span
        className={`text-2xl font-bold leading-none ${
          isLoading
            ? "text-neutral-secondary-mid"
            : isClickable
              ? "text-brick-primary"
              : "text-neutral-secondary-mid"
        }`}
      >
        {displayValue}
      </span>

      {/* Label */}
      <span className="text-xs font-medium uppercase tracking-wide text-neutral-secondary-mid">
        Units met meerdere contracten
      </span>

      {/* Hint when actionable */}
      {isClickable && !isLoading && (
        <span className="text-xs text-neutral-secondary-mid mt-0.5">
          Klik om te bekijken
        </span>
      )}
    </div>
  );

  if (!isClickable) {
    return tile;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{tile}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" sideOffset={8}>
        <div className="px-4 py-3 border-b">
          <p className="text-sm font-semibold text-neutral-primary">
            Units met meerdere actieve contracten
          </p>
          <p className="text-xs text-neutral-secondary-mid mt-0.5">
            {count} {count === 1 ? "unit" : "units"} gevonden
          </p>
        </div>

        <ul className="max-h-72 overflow-y-auto divide-y">
          {duplicateUnits.map((entry) => (
            <DuplicateUnitRow
              key={entry.unitId}
              entry={entry}
              onNavigate={handleBuildingNav}
            />
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

// ─── Row inside the popover ───────────────────────────────────────────────────

interface DuplicateUnitRowProps {
  entry: DuplicateUnit;
  onNavigate: (buildingId: string) => void;
}

const DuplicateUnitRow: React.FC<DuplicateUnitRowProps> = ({
  entry,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const hasBuilding = entry.buildingId != null;

  const header = hasBuilding ? (
    <button
      onClick={() => onNavigate(entry.buildingId!)}
      className="flex items-center gap-1.5 w-full text-left rounded px-1 -mx-1 hover:bg-neutral-50 transition-colors cursor-pointer"
    >
      <Warning weight="fill" size={12} className="text-brick-primary shrink-0" />
      <span className="text-sm font-semibold text-neutral-primary">
        Unit {entry.unitId}
      </span>
      <span className="text-xs text-neutral-secondary-mid">
        — {entry.contractCount} contracten
      </span>
    </button>
  ) : (
    <TooltipProvider>
      <ShadcnTooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-default">
            <Warning weight="fill" size={12} className="text-brick-primary shrink-0" />
            <span className="text-sm font-semibold text-neutral-primary">
              Unit {entry.unitId}
            </span>
            <span className="text-xs text-neutral-secondary-mid">
              — {entry.contractCount} contracten
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>Geen gebouw bekend</p>
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );

  return (
    <li className="px-4 py-2.5">
      {header}
      <div className="mt-1 flex flex-wrap gap-1">
        {entry.contractIds.map((id) => (
          <button
            key={id}
            onClick={() => navigate(`/rental/contracts/${id}`)}
            className="text-xs text-moss-primary underline decoration-dotted hover:text-moss-primary/80 transition-colors"
          >
            #{id}
          </button>
        ))}
      </div>
    </li>
  );
};
